import React from "react";
import { ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/content";

const Hero = () => {
  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden grain bg-[#0B0410]"
    >
      {/* Hexagonal pattern background — matches EMB logo geometry */}
      <div className="absolute inset-0 opacity-[0.7] pointer-events-none" data-testid="hero-hex-pattern">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          aria-hidden="true"
        >
          <defs>
            <filter id="hexBloom" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="1.6" result="b1" />
              <feGaussianBlur stdDeviation="4" in="SourceGraphic" result="b2" />
              <feMerge>
                <feMergeNode in="b2" />
                <feMergeNode in="b1" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <pattern
              id="hexPattern"
              width="56"
              height="97"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M28 0 L56 16 L56 49 L28 65 L0 49 L0 16 Z"
                fill="none"
                stroke="#FBEFD3"
                strokeWidth="1.1"
                opacity="1"
              />
              <path
                d="M28 32.5 L56 48.5 L56 81.5 L28 97 L0 81.5 L0 48.5 Z"
                fill="none"
                stroke="#FBEFD3"
                strokeWidth="1.1"
                opacity="0.8"
              />
            </pattern>
            <radialGradient id="hexVignette" cx="50%" cy="50%" r="75%">
              <stop offset="0%" stopColor="#3D1565" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#0B0410" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g filter="url(#hexBloom)">
            <rect width="100%" height="100%" fill="url(#hexPattern)" />
          </g>
          <rect width="100%" height="100%" fill="url(#hexVignette)" />
        </svg>
      </div>

      {/* Decorative glowing hex (oversized accent) */}
      <div
        className="absolute -right-32 -bottom-32 w-[600px] h-[600px] opacity-30 pointer-events-none animate-float-slow"
        aria-hidden="true"
      >
        <svg viewBox="0 0 200 230" className="w-full h-full">
          <defs>
            <linearGradient id="hexGlow" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3D1565" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#E6D5B8" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <path
            d="M100 0 L200 58 L200 173 L100 230 L0 173 L0 58 Z"
            fill="url(#hexGlow)"
            stroke="#E6D5B8"
            strokeWidth="0.4"
            strokeOpacity="0.6"
          />
        </svg>
      </div>

      {/* Multiple overlays for depth */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0410] via-[#0B0410]/85 to-[#0B0410]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0410] via-transparent to-[#0B0410]/40" />

      {/* Decorative side bar */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#3D1565]/40 to-transparent hidden lg:block" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-32 lg:py-40 w-full">
        <div className="max-w-3xl">
          <div
            className="reveal flex items-center gap-3 mb-8"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-[#E6D5B8]" />
            <span className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium">
              Finanzas · Inversión · Protección
            </span>
          </div>

          <h1
            className="reveal reveal-delay-1 font-display text-5xl sm:text-6xl lg:text-7xl text-[#F9F6F0] leading-[1.05] font-light"
            data-testid="hero-headline"
          >
            Tu patrimonio,{" "}
            <span className="italic text-[#E6D5B8]">construido</span> con
            disciplina institucional.
          </h1>

          <p
            className="reveal reveal-delay-2 mt-8 text-base sm:text-lg text-[#F9F6F0]/75 leading-relaxed max-w-2xl font-light"
            data-testid="hero-subheadline"
          >
            Asesoría en finanzas personales, ahorro, inversión y protección.
            Promotores autorizados de Allianz México, con claves directas en
            Skandia y Dominion Capital Strategies — y acceso a las principales
            aseguradoras reguladas del país.
          </p>

          <div className="reveal reveal-delay-3 mt-12 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => go("contacto")}
              className="group bg-[#3D1565] hover:bg-[#4a1c79] text-white px-8 py-4 rounded-sm transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              <span className="text-sm tracking-wider uppercase font-medium">
                Agendar asesoría
              </span>
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => go("servicios")}
              className="border border-[#F9F6F0]/25 hover:border-[#E6D5B8] hover:text-[#E6D5B8] text-[#F9F6F0] px-8 py-4 rounded-sm transition-all duration-300 text-sm tracking-wider uppercase font-medium"
            >
              Conocer servicios
            </button>
          </div>

          {/* Trust line */}
          <div
            className="reveal reveal-delay-4 mt-16 pt-8 border-t border-white/10 grid grid-cols-3 gap-6 max-w-2xl"
            data-testid="hero-trust-strip"
          >
            <div>
              <div className="font-display text-3xl text-[#E6D5B8]">10+</div>
              <div className="text-xs text-[#A397B4] uppercase tracking-widest mt-1">
                Aseguradoras
              </div>
            </div>
            <div>
              <div className="font-display text-3xl text-[#E6D5B8]">3</div>
              <div className="text-xs text-[#A397B4] uppercase tracking-widest mt-1">
                Casas de inversión
              </div>
            </div>
            <div>
              <div className="font-display text-3xl text-[#E6D5B8]">100%</div>
              <div className="text-xs text-[#A397B4] uppercase tracking-widest mt-1">
                Regulado
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
